import { useUserStore } from '../zustand/UserStore';

export type UserRole = 'User' | 'Admin' | 'Super admin';

interface AuthorizationPermissions {
  canViewAllUsers: boolean;
  canViewAllCalls: boolean;
  canManageUsers: boolean;
  canAccessUserDashboard: (targetUserId?: string) => boolean;
  canAccessCallAnalysis: (callData?: any) => boolean;
  canAccessSettings: boolean;
  canAccessMainDashboard: boolean;
  canAccessAnalysisDashboard: boolean;
  accessibleUserIds: string[];
}

export const useAuthorization = (): AuthorizationPermissions => {
  const user = useUserStore((state) => state.user);

  if (!user) {
    return {
      canViewAllUsers: false,
      canViewAllCalls: false,
      canManageUsers: false,
      canAccessUserDashboard: () => false,
      canAccessCallAnalysis: () => false,
      canAccessSettings: false,
      canAccessMainDashboard: false,
      canAccessAnalysisDashboard: false,
      accessibleUserIds: []
    };
  }

  const isSuperAdmin = user.role === 'Super admin';
  const isAdmin = user.role === 'Admin';

  return {
    // Super admin can view all users, Admin can view subordinates, User can only view self
    canViewAllUsers: isSuperAdmin,
    
    // Super admin can view all calls, Admin can view subordinate calls, User can only view own calls
    canViewAllCalls: isSuperAdmin,
    
    // Only Super admin and Admin can manage users
    canManageUsers: isSuperAdmin || isAdmin,
    
    // Check if user can access specific user's dashboard
    canAccessUserDashboard: (targetUserId?: string) => {
      if (isSuperAdmin) return true;
      if (isAdmin) {
        // Admin can access their own dashboard and subordinates
        if (!targetUserId || targetUserId === user.uid) return true;
        // Here we would normally check if targetUserId is a subordinate
        // For now, we'll assume admin can access all (this should be checked via API)
        return true;
      }
      // Regular users can only access their own dashboard
      return !targetUserId || targetUserId === user.uid;
    },
    
    // Check if user can access specific call analysis
    canAccessCallAnalysis: (callData?: any) => {
      if (isSuperAdmin) return true;
      if (isAdmin) {
        // Admin can access calls from their subordinates
        if (!callData) return true; // Allow access to check via API
        return callData.representative_id === user.uid || 
               (callData.representative_head === user.uid);
      }
      // Regular users can only access their own calls
      if (!callData) return true; // Allow access to check via API
      return callData.representative_id === user.uid;
    },
    
    // Settings access control
    canAccessSettings: isSuperAdmin || isAdmin,
    
    // Main dashboard access - only for admin roles (Overview)
    canAccessMainDashboard: isSuperAdmin || isAdmin,
    
    // Analysis dashboard - where new analyses are created
    canAccessAnalysisDashboard: isSuperAdmin || isAdmin,
    
    // List of user IDs this user can access (would be populated via API)
    accessibleUserIds: [user.uid] // Will be updated via API calls
  };
};

export const getUserRoleDisplayName = (role: UserRole): string => {
  switch (role) {
    case 'Super admin':
      return 'Super Administrator';
    case 'Admin':
      return 'Administrator';
    case 'User':
      return 'User';
    default:
      return role;
  }
};

export const getRoleColor = (role: UserRole): string => {
  switch (role) {
    case 'Super admin':
      return 'bg-red-500 text-white';
    case 'Admin':
      return 'bg-purple-500 text-white';
    case 'User':
      return 'bg-blue-500 text-white';
    default:
      return 'bg-gray-400 text-white';
  }
};

// Navigation permissions based on role
export const getNavigationPermissions = (role: UserRole, userId?: string) => {
  const isSuperAdmin = role === 'Super admin';
  const isAdmin = role === 'Admin';
  const isUser = role === 'User';
  
  return {
    showMainDashboard: isSuperAdmin || isAdmin, // Only admin roles can see Overview
    showUserDashboard: isUser, // Only regular users can see My Dashboard
    showAnalysisDashboard: isSuperAdmin || isAdmin, // Only admin roles can create analyses
    showSettings: isSuperAdmin || isAdmin, // Only admin roles can manage users
    defaultRoute: isAdmin || isSuperAdmin ? '/main' : `/user/${userId}` // Default landing page
  };
};
